import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";

const stripHtml = (s) => new DOMParser().parseFromString(String(s || ""), "text/html").body.textContent || "";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);
  const notifiedRef = useRef(new Set());
  const firstRef = useRef(true);
  const navigate = useNavigate();

  const loadCount = () => api.get("/notifications/unread_count").then(({ data }) => setUnread(data.count)).catch(() => {});
  const loadItems = () => api.get("/notifications").then(({ data }) => {
    setItems(data); setLoaded(true);
    const canPush = typeof Notification !== "undefined" && Notification.permission === "granted";
    if (firstRef.current) { data.forEach((n) => notifiedRef.current.add(n.notification_id)); firstRef.current = false; return; }
    data.forEach((n) => {
      if (!notifiedRef.current.has(n.notification_id)) {
        notifiedRef.current.add(n.notification_id);
        if (canPush && !n.read) { try { new Notification(n.title, { body: (n.body || "").replace(/<[^>]+>/g, "") }); } catch { /* ignore */ } }
      }
    });
  }).catch(() => {});

  useEffect(() => {
    loadCount(); loadItems();
    const iv = setInterval(() => { loadCount(); loadItems(); }, 20000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      await loadItems();
      if (unread > 0) { await api.post("/notifications/read_all").catch(() => {}); setUnread(0); }
    }
  };
  const go = (n) => { setOpen(false); if (n.link) navigate(n.link); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle} className="relative grid h-9 w-9 place-items-center rounded-full border border-gray-200 text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]">
        <Bell size={17} />
        {unread > 0 && <span data-testid="notif-badge" className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-[#FF3B30] px-1 text-[10px] font-bold text-white">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <span className="font-display font-bold">Notifications</span>
            {typeof Notification !== "undefined" && Notification.permission !== "granted" && (
              <button data-testid="enable-push" onClick={() => Notification.requestPermission()} className="rounded-full bg-[#002FA7] px-2.5 py-1 text-[10px] font-bold text-white">Enable push</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400" data-testid="notif-empty">{loaded ? "No notifications yet" : "Loading…"}</div>
            ) : items.map((n) => (
              <button key={n.notification_id} data-testid={`notif-${n.notification_id}`} onClick={() => go(n)}
                className={`block w-full border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${!n.read ? "bg-[#002FA7]/5" : ""}`}>
                <div className="text-sm font-bold text-gray-900">{n.title}</div>
                <div className="mt-0.5 line-clamp-2 text-xs text-gray-500">{stripHtml(n.body)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
